import { GenericSelect } from '@/components/GenericSelect';
import { checkMaterialEditInput, EditMaterialInputType } from '@shelflix/schemas/material/edit';
import { DisplayDto } from '@shelflix/trpc/routes/material/listDisplays/useCase';
import { Badge } from '@shelflix/ui-web/components/ui/badge';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@shelflix/ui-web/components/ui/form';
import { Input } from '@shelflix/ui-web/components/ui/input';
import { useEffect } from 'react';
import { UseFormReturn } from 'react-hook-form';

interface DisplayInfoSectionProps {
  form: UseFormReturn<
    EditMaterialInputType & {
      cnpj: {
        uuid: string;
        cnpj: string;
        name: string;
      };
      materialTypeUuid: {
        uuid: string;
        name: string;
        brand: {
          name: string;
        };
      };
    }
  >;
  displays: DisplayDto[];
  canEdit: boolean;
}

export function DisplayInfoSection({ form, displays, canEdit }: DisplayInfoSectionProps) {
  const canEditMaterial = canEdit;
  const code = form.watch('display.code');
  const externalId = form.watch('display.externalId');

  // The id survives a rename in MagicInfo, the code (device name) does not
  const currentDisplay =
    (externalId ? displays.find((d) => d.id === externalId) : undefined) ??
    (code ? displays.find((d) => d.code === code) : undefined);
  const isUnmatched = displays.length > 0 && !currentDisplay;
  // An empty list means MagicInfo failed or has no devices, so the stored values stay untouched and read-only
  const isListUnavailable = displays.length === 0;
  const areTextFieldsLocked = !canEditMaterial || isUnmatched || isListUnavailable;

  useEffect(() => {
    if (currentDisplay) {
      form.setValue('display.code', currentDisplay.code);
      // MagicInfo can omit fields; keep the stored value when the list has none
      if (currentDisplay.brand) form.setValue('display.brand', currentDisplay.brand);
      if (currentDisplay.deviceModelName) form.setValue('display.model', currentDisplay.deviceModelName);
      if (currentDisplay.screenOrientation) form.setValue('display.orientation', currentDisplay.screenOrientation);
      if (currentDisplay.deviceType) form.setValue('display.type', currentDisplay.deviceType);
      if (currentDisplay.screenSize) form.setValue('display.size', currentDisplay.screenSize);
    } else if (isUnmatched) {
      // form.reset would restore the stored values, which belong to a device MagicInfo no longer lists
      form.setValue('display.code', '');
      form.setValue('display.brand', '');
      form.setValue('display.model', '');
      form.setValue('display.orientation', '');
      form.setValue('display.type', '');
      form.setValue('display.size', '');
      form.setValue('display.externalId', '');
    }
  }, [currentDisplay, isUnmatched, code, externalId]);

  return (
    <div className="flex w-full flex-col gap-4">
      {isListUnavailable && (
        <p className="text-sm text-[var(--gray-primary)]">
          A lista de telas está indisponível no momento. Os dados guardados são exibidos somente para leitura.
        </p>
      )}
      <div className="flex w-full flex-col items-end gap-4 sm:flex-row">
        <FormField
          control={form.control}
          name="display.code"
          rules={{ validate: (value) => checkMaterialEditInput('display.code', value) }}
          render={({ field: _field }) => (
            <FormItem className="flex w-full flex-col gap-[3px]">
              <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">Identificador da tela</FormLabel>
              <FormControl>
                {isListUnavailable ? (
                  <Input className="h-10" disabled value={code ?? ''} />
                ) : (
                  <GenericSelect<DisplayDto>
                    listOnEmptySearch
                    disabled={!isUnmatched || !canEditMaterial}
                    fetchItems={(search) => {
                      const items = displays.map((d) => ({
                        ...d,
                        disabled: !!d.currentMaterialAssociation,
                      }));
                      return !search
                        ? Promise.resolve(items)
                        : Promise.resolve(items.filter((d) => d.code.toLowerCase().includes(search.toLowerCase())));
                    }}
                    onSearchChange={() => {}}
                    value={currentDisplay}
                    onSelect={(item: DisplayDto) => {
                      if (item.currentMaterialAssociation) return;
                      form.setValue('display.code', item.code);
                      form.setValue('display.brand', item.brand);
                      form.setValue('display.model', item.deviceModelName);
                      form.setValue('display.orientation', item.screenOrientation);
                      form.setValue('display.type', item.deviceType);
                      form.setValue('display.size', item.screenSize);
                      form.setValue('display.externalId', item.id);
                      form.trigger('display');
                    }}
                    renderItem={(item: DisplayDto) => (
                      <div
                        className={`flex w-full flex-col gap-2 ${item.currentMaterialAssociation ? 'cursor-disabled opacity-40' : ''}`}
                      >
                        <div className="flex w-full items-center">
                          <span>{item.code}</span>
                          <div className="ml-auto flex flex-col">
                            <Badge variant={item.status.toUpperCase() === 'ACTIVE' ? 'default' : 'destructive'}>
                              {item.status.toUpperCase() === 'ACTIVE' ? 'ONLINE' : item.status}
                            </Badge>
                          </div>
                        </div>
                        {item.currentMaterialAssociation && (
                          <div>
                            <span className="text-[10px]">
                              Dispositivo em uso em {item.currentMaterialAssociation.facilityName}
                              {item.currentMaterialAssociation.exhibitionPoint
                                ? ` (${item.currentMaterialAssociation.exhibitionPoint})`
                                : ''}{' '}
                              - {item.currentMaterialAssociation.code}
                            </span>
                          </div>
                        )}
                        {!item.currentMaterialAssociation && (
                          <div>
                            <span className="text-[13px] text-[var(--active-dark)]">Disponível</span>
                          </div>
                        )}
                      </div>
                    )}
                    getItemLabel={(item: DisplayDto) => item.code}
                    getItemKey={(item: DisplayDto) => item.code}
                  />
                )}
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="display.brand"
          // rules={{ validate: (value) => checkMaterialEditInput('display.brand', value) }}
          render={({ field }) => (
            <FormItem className="flex w-full flex-col gap-[3px]">
              <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">Marca do Monitor</FormLabel>
              <FormControl>
                <Input className="h-10" placeholder="Marca X" disabled={areTextFieldsLocked} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="display.model"
          rules={{ validate: (value) => checkMaterialEditInput('display.model', value) }}
          render={({ field }) => (
            <FormItem className="flex w-full flex-col gap-[3px]">
              <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">Modelo</FormLabel>
              <FormControl>
                <Input className="h-10" placeholder="Ex: LG 556UH-F" disabled={areTextFieldsLocked} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <div className="flex w-full flex-col items-end gap-4 sm:flex-row">
        <FormField
          control={form.control}
          name="display.orientation"
          rules={{ validate: (value) => checkMaterialEditInput('display.orientation', value) }}
          render={({ field }) => (
            <FormItem className="flex w-full flex-col gap-[3px]">
              <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">Orientação</FormLabel>
              <FormControl>
                <Input className="h-10" placeholder="Selecione uma opção" disabled={areTextFieldsLocked} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormItem className="flex w-full flex-col gap-[3px]">
          <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">Device ID</FormLabel>
          <FormControl>
            <Input
              className="h-10"
              disabled
              value={currentDisplay?.id ?? (isListUnavailable ? externalId : undefined)}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
        <FormField
          control={form.control}
          name="display.size"
          rules={{ validate: (value) => checkMaterialEditInput('display.size', value) }}
          render={({ field }) => (
            <FormItem className="flex w-full flex-col gap-[3px]">
              <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">Tamanho de Tela</FormLabel>
              <FormControl>
                <Input className="h-10" placeholder="Ex: 40" disabled={areTextFieldsLocked} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  );
}
